import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Loader2, Info } from 'lucide-react';
import * as React from 'react';
import { authClient } from '@/lib/auth-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { PasswordInput } from '@/components/auth/password-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/register')({
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (session.data) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: RegisterPage,
});

const PERSONAL_DOMAINS = [
  'gmail.com',
  'wp.pl',
  'o2.pl',
  'interia.pl',
  'onet.pl',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
];

const registerSchema = z.object({
  email: z.string().min(1, 'required').email('invalidEmail'),
  password: z.string().min(10, 'passwordMin').max(128, 'passwordMax'),
});

type RegisterForm = z.infer<typeof registerSchema>;

function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [strengthScore, setStrengthScore] = React.useState(0);
  const [zxcvbnLoaded, setZxcvbnLoaded] = React.useState(false);
  const zxcvbnRef = React.useRef<typeof import('@zxcvbn-ts/core').zxcvbn | null>(null);

  // Lazy load zxcvbn
  React.useEffect(() => {
    let cancelled = false;
    async function loadZxcvbn() {
      const [core, commonPkg, plPkg] = await Promise.all([
        import('@zxcvbn-ts/core'),
        import('@zxcvbn-ts/language-common'),
        import('@zxcvbn-ts/language-pl'),
      ]);
      if (cancelled) return;
      core.zxcvbnOptions.setOptions({
        graphs: commonPkg.adjacencyGraphs,
        dictionary: {
          ...commonPkg.dictionary,
          ...plPkg.dictionary,
        },
        translations: plPkg.translations,
      });
      zxcvbnRef.current = core.zxcvbn;
      setZxcvbnLoaded(true);
    }
    loadZxcvbn();
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    defaultValues: { email: '', password: '' },
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  // Password strength evaluation
  React.useEffect(() => {
    if (!passwordValue || passwordValue.length === 0) {
      setStrengthScore(0);
      return;
    }
    if (!zxcvbnRef.current) return;
    const evaluate = () => {
      const result = zxcvbnRef.current!(passwordValue);
      // zxcvbn returns 0-4, map to 1-3 for our UI
      const mapped = result.score <= 1 ? 1 : result.score <= 2 ? 2 : 3;
      setStrengthScore(mapped);
    };
    const timeout = setTimeout(evaluate, 150);
    return () => clearTimeout(timeout);
  }, [passwordValue, zxcvbnLoaded]);

  const isPersonalDomain = React.useMemo(() => {
    if (!emailValue.includes('@')) return false;
    const domain = emailValue.split('@')[1]?.toLowerCase();
    return PERSONAL_DOMAINS.includes(domain);
  }, [emailValue]);

  const onSubmit = async (data: RegisterForm) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      const result = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: '', // empty — onboarding wizard step 1 sets real name
      });

      if (result.error) {
        // Anti-enumeration: generic error regardless of cause
        setServerError(t('auth.registerGenericError'));
        return;
      }

      // Create default workspace via BA organization plugin.
      // Uses email prefix as temporary slug; user sets real name in onboarding step 2.
      const slug = data.email
        .split('@')[0]
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .slice(0, 30);
      const orgResult = await authClient.organization.create({
        name: slug, // temporary — replaced in onboarding step 2
        slug,
      });

      if (orgResult.data) {
        await authClient.organization.setActive({ organizationId: orgResult.data.id });
      }

      // After successful sign-up, redirect to onboarding wizard
      await navigate({ to: '/onboarding' });
    } catch {
      setServerError(t('auth.registerGenericError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      {t('auth.hasAccount')}{' '}
      <Link to="/login" className="font-medium text-primary underline-offset-4 hover:underline">
        {t('auth.loginLink')}
      </Link>
    </>
  );

  return (
    <AuthLayout
      title={t('auth.registerTitle')}
      description={t('auth.registerDescription')}
      footer={footer}
    >
      {/* AI Act banner */}
      <div className="flex gap-3 rounded-lg border border-primary/15 bg-primary/5 p-3.5 dark:border-primary/20 dark:bg-primary/10">
        <Info className="mt-0.5 size-4 shrink-0 text-primary" />
        <p className="text-xs leading-relaxed text-foreground/70">{t('auth.aiActBanner')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label
              htmlFor="email"
              className="text-xs font-semibold uppercase tracking-wider text-foreground"
            >
              {t('auth.emailLabel')}
            </Label>
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              aria-invalid={!!errors.email}
              aria-describedby={
                errors.email ? 'email-error' : isPersonalDomain ? 'email-warning' : undefined
              }
              {...register('email')}
            />
            {errors.email && (
              <p id="email-error" className="text-xs text-destructive" role="alert">
                {t(`auth.validation.${errors.email.message}`)}
              </p>
            )}
            {isPersonalDomain && !errors.email && (
              <p id="email-warning" className="text-xs text-amber-600 dark:text-amber-400">
                {t('auth.personalDomainWarning')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-xs font-semibold uppercase tracking-wider text-foreground"
            >
              {t('auth.passwordLabel')}
            </Label>
            <PasswordInput
              id="password"
              showStrength
              strengthScore={strengthScore}
              placeholder={t('auth.passwordPlaceholder')}
              className="h-12 rounded-lg bg-background text-sm"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              value={passwordValue}
              {...register('password')}
            />
            {errors.password && (
              <p id="password-error" className="text-xs text-destructive" role="alert">
                {t(`auth.validation.${errors.password.message}`)}
              </p>
            )}
          </div>
        </div>

        {serverError && (
          <div
            className="mt-5 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
            role="alert"
            aria-live="assertive"
          >
            <p className="text-sm text-destructive">{serverError}</p>
          </div>
        )}

        <Button
          type="submit"
          className="mt-8 h-12 w-full cursor-pointer rounded-full text-sm font-semibold uppercase tracking-wider"
          size="lg"
          disabled={isSubmitting}
          aria-disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t('auth.registerSubmitting')}
            </>
          ) : (
            t('auth.registerSubmit')
          )}
        </Button>

        <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
          {t('auth.termsNotice')}{' '}
          <a
            href="/regulamin"
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer underline underline-offset-4 hover:text-foreground"
          >
            {t('auth.termsLink')}
          </a>{' '}
          {t('auth.and')}{' '}
          <a
            href="/polityka-prywatnosci"
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer underline underline-offset-4 hover:text-foreground"
          >
            {t('auth.privacyLink')}
          </a>
          .
        </p>
      </form>
    </AuthLayout>
  );
}
